import {
  BookOpen,
  FileText,
  Headphones,
  Map,
  Quote,
  Star,
  Target,
  UtensilsCrossed,
  Video,
  type LucideIcon,
} from "lucide-react";
import { ContentStatus, StudioContentStatus, TagGroupType } from "./schema";

export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "pt", label: "Portuguese" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "it", label: "Italian" },
  { code: "nl", label: "Dutch" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
  { code: "zh", label: "Chinese" },
  { code: "ar", label: "Arabic" },
  { code: "hi", label: "Hindi" },
  { code: "ru", label: "Russian" },
  { code: "tr", label: "Turkish" },
  { code: "sv", label: "Swedish" },
  { code: "no", label: "Norwegian" },
  { code: "da", label: "Danish" },
  { code: "fi", label: "Finnish" },
  { code: "pl", label: "Polish" },
  { code: "th", label: "Thai" },
] as const;

// Horoscope readings are localized only in en/pt/es/fr per server allowlist.
export const HOROSCOPE_LANGUAGES = SUPPORTED_LANGUAGES;

export type SupportedLanguageCode =
  (typeof SUPPORTED_LANGUAGES)[number]["code"];

export function getLanguageLabel(code: string): string {
  return (
    SUPPORTED_LANGUAGES.find((l) => l.code === code)?.label ??
    code.toUpperCase()
  );
}

export const STATUS_CONFIG: Record<
  StudioContentStatus | ContentStatus,
  {
    label: string;
    variant: "default" | "secondary" | "outline" | "destructive";
  }
> = {
  draft: { label: "Draft", variant: "secondary" },
  submitted: { label: "Submitted", variant: "outline" },
  // scheduled: { label: "Scheduled", variant: "default" },
  transcoding: { label: "Transcoding", variant: "outline" },
  published: { label: "Published", variant: "default" },
  changes_requested: { label: "Changes Requested", variant: "destructive" },
  archived: { label: "Archived", variant: "secondary" },
  failed: { label: "Failed", variant: "destructive" },
  outdated: { label: "Outdated", variant: "secondary" },
};

export interface ContentTypeConfig {
  key: string;
  label: string;
  pluralLabel: string;
  icon: LucideIcon;
  apiPath: string;
  listRoute: string;
  createRoute: string;
  editRoute: (id: string) => string;
}

export const CONTENT_TYPE_CONFIG: Record<string, ContentTypeConfig> = {
  audio: {
    key: "audio",
    label: "Audio",
    pluralLabel: "Audio",
    icon: Headphones,
    apiPath: "/audio",
    listRoute: "/studio/audio",
    createRoute: "/studio/audio/new",
    editRoute: (id) => `/studio/audio/${id}/edit`,
  },
  workbook: {
    key: "workbook",
    label: "Workbook",
    pluralLabel: "Workbooks",
    icon: BookOpen,
    apiPath: "/content-workbooks",
    listRoute: "/studio/workbooks",
    createRoute: "/studio/workbooks/new",
    editRoute: (id) => `/studio/workbooks/${id}/edit`,
  },
  video: {
    key: "video",
    label: "Video",
    pluralLabel: "Videos",
    icon: Video,
    apiPath: "/video",
    listRoute: "/studio/video",
    createRoute: "/studio/video/new",
    editRoute: (id) => `/studio/video/${id}/edit`,
  },
  blog: {
    key: "blog",
    label: "Blog",
    pluralLabel: "Blogs",
    icon: FileText,
    apiPath: "/content-blogs",
    listRoute: "/studio/blogs",
    createRoute: "/studio/blogs/new",
    editRoute: (id) => `/studio/blogs/${id}/edit`,
  },
  recipe: {
    key: "recipe",
    label: "Recipe",
    pluralLabel: "Recipes",
    icon: UtensilsCrossed,
    apiPath: "/content-recipes",
    listRoute: "/studio/recipes",
    createRoute: "/studio/recipes/new",
    editRoute: (id) => `/studio/recipes/${id}/edit`,
  },
  affirmation: {
    key: "affirmation",
    label: "Affirmation",
    pluralLabel: "Affirmations",
    icon: Quote,
    apiPath: "/content-affirmations",
    listRoute: "/studio/affirmations",
    createRoute: "/studio/affirmations/new",
    editRoute: (id) => `/studio/affirmations/${id}/edit`,
  },
  journey: {
    key: "journey",
    label: "Journey",
    pluralLabel: "Journeys",
    icon: Map,
    apiPath: "/content-journeys",
    listRoute: "/studio/journeys",
    createRoute: "/studio/journeys/new",
    editRoute: (id) => `/studio/journeys/${id}/edit`,
  },
  astrology: {
    key: "astrology",
    label: "Horoscope",
    pluralLabel: "Horoscopes",
    icon: Star,
    apiPath: "/horoscope/entries",
    listRoute: "/studio/horoscope",
    createRoute: "/studio/horoscope/create",
    editRoute: (id) => `/studio/horoscope/edit/${id}`,
  },
  challenge: {
    key: "challenge",
    label: "Challenge",
    pluralLabel: "Challenges",
    icon: Target,
    apiPath: "/content-challenges",
    listRoute: "/studio/challenges",
    createRoute: "/studio/challenges/new",
    editRoute: (id) => `/studio/challenges/${id}/edit`,
  },
};

export function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export const GROUP_TYPE_LABELS: Record<TagGroupType, string> = {
  mood: "Mood Type",
  feeling: "Feeling Type",
  shift: "Shift Labels",
  cycle: "Cycle Phase",
  energy: "Energy Level",
  theme: "Theme",
  goal: "Goal",
  sensitivity: "Sensitivity",
  spiritual: "Spiritual Tone",
  custom: "Custom",
};

// Quality Monitor issue types → readable labels (EVO-61).
export const ISSUE_TYPE_LABELS: Record<string, string> = {
  missing_category: "Missing category",
  missing_tags: "No tags",
  single_language: "Single language",
  outdated_content: "Outdated content",
};
